import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import Popup from '../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../hooks/useStore';
import CommonButton from '../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../components/form-components/common-components/dropdown/FormikDropdown';
import styles from './EditPersonPopup.module.css';
import { PersonSchema } from '../../../../schemas/PersonSchema';
import { PersonFormValues } from '../../../../models/person/PersonModel';
import { toast } from 'react-toastify';

const PopupFooter = ({ onClose }: { onClose: () => void }) => {
  const { isSubmitting, submitForm } = useFormikContext<PersonFormValues>();
  const { uiStore } = useStore();

  return (
    <>
      <CommonButton type="cancel" onClick={onClose} disabled={isSubmitting}>
        İptal
      </CommonButton>
      <CommonButton
        type="save"
        onClick={submitForm}
        disabled={isSubmitting}
        loading={uiStore.isLoading}
      >
        Güncelle
      </CommonButton>
    </>
  );
};

const EditPersonPopup = () => {
  const { popupStore, hospitalStore, uiStore, personStore } = useStore();

  useEffect(() => {
    const fetchHospitals = async () => {
      if (!popupStore.isEditPersonOpen) return;

      try {
        uiStore.setLoading(true);
        await hospitalStore.listAllHospitals();
      } catch (err) {
        console.error('Error fetching hospitals:', err);
      } finally {
        uiStore.setLoading(false);
      }
    };
    fetchHospitals();
  }, [hospitalStore, uiStore, popupStore.isEditPersonOpen]);

  const hospitalOptions = useMemo(
    () =>
      hospitalStore.hospitalList
        .filter(
          (hospital): hospital is { id: number; hospitalName: string } =>
            hospital.id !== undefined && hospital.hospitalName !== undefined
        )
        .map(hospital => ({
          value: hospital.id,
          label: hospital.hospitalName,
        })),
    [hospitalStore.hospitalList]
  );

  const initialValues: PersonFormValues = {
    name: personStore.selectedPersonToEdit?.name || '',
    hospitalId: personStore.selectedPersonToEdit?.hospitalId || null,
    phoneNumber: personStore.selectedPersonToEdit?.phoneNumber || '',
    email: personStore.selectedPersonToEdit?.email || '',
  };

  const handleSubmit = async (values: PersonFormValues) => {
    try {
      if (!personStore.selectedPersonToEdit?.id) {
        toast.error('Kişi ID bulunamadı');
        return;
      }

      if (!values.hospitalId) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      const response = await personStore.updatePerson(personStore.selectedPersonToEdit.id, values);
      if (response.error === false) {
        toast.success('Kişi başarıyla güncellendi.');
        popupStore.setIsEditPersonOpen(false);
        personStore.setSelectedPersonToEdit(null);
        personStore.refreshGrid();
      } else {
        toast.error('Kişi güncellenemedi. ' + response.message);
      }
    } catch (error) {
      console.error('Kişi güncellenirken hata:', error);
      toast.error('Kişi güncellenirken bir hata oluştu');
    }
  };

  const handleClose = () => {
    popupStore.setIsEditPersonOpen(false);
    personStore.setSelectedPersonToEdit(null);
    toast.dismiss();
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={PersonSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {() => (
        <Popup
          isOpen={popupStore.isEditPersonOpen}
          onClose={handleClose}
          title="Kişi Düzenle"
          footer={() => <PopupFooter onClose={handleClose} />}
        >
          <div className={styles.container}>
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikTextField name="name" label="İsim" placeholder="İsim giriniz" />
              </div>

              <div className={styles.inputContainer}>
                <FormikDropdown name="hospitalId" options={hospitalOptions} label="Hastane" />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="phoneNumber"
                  label="Telefon Numarası"
                  placeholder="(5XX) XXX XX XX"
                  type="tel"
                  mask={(value: string) => {
                    const numbers = value.replace(/\D/g, '');
                    if (numbers.length <= 3) {
                      return numbers.length > 0 ? `(${numbers}` : '';
                    } else if (numbers.length <= 6) {
                      return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
                    } else if (numbers.length <= 8) {
                      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(6)}`;
                    } else {
                      return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)} ${numbers.slice(
                        6,
                        8
                      )} ${numbers.slice(8, 10)}`;
                    }
                  }}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="email"
                  label="E-mail"
                  placeholder="E-mail giriniz (opsiyonel)"
                  type="email"
                />
              </div>
            </Form>
          </div>
        </Popup>
      )}
    </Formik>
  );
};

export default observer(EditPersonPopup);
